<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['selectMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

/**
 * Computed boolean indicating if the editor is in private note mode
 * When isReplyRestricted is true, force switch to private note
 * Otherwise, respect the current mode prop
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => {
  if (props.isReplyRestricted) {
    // Force switch to private note when replies are restricted
    return true;
  }
  // Otherwise respect the current mode
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  const xTranslate = isPrivate.value ? replyModeSize.width.value + 16 : 0;

  return `${xTranslate}px`;
});
</script>

<template>
  <!--
    Two independent buttons, not one toggle. A single "flip the mode" button
    used to sit here — pressing REPLY while already on REPLY silently sent you
    to NOTE, because the handler only ever knew how to invert, never which
    label was actually under the thumb. Each label now sets its own mode
    explicitly, so pressing the one you're already on is a no-op (the parent's
    setReplyMode also short-circuits on no-op, but this is correct either way).
  -->
  <div
    class="flex items-center flex-shrink-0 w-auto h-8 p-1 text-xs md:text-sm rounded-full bg-n-alpha-2 group relative z-0"
    :class="{ 'cursor-not-allowed': disabled || isReplyRestricted }"
  >
    <!-- whitespace-nowrap is load-bearing: both labels render at all times (the
         pill just slides a chip over the active one), so in pt-BR the pair is
         long enough that a phone would wrap "Mensagem Privada" onto a second
         line and blow the pill's fixed h-8 apart. Keep px-2 here — the chip
         width math in this file adds a hardcoded 16px for it. -->
    <button
      ref="wootEditorReplyMode"
      type="button"
      :disabled="disabled || isReplyRestricted"
      class="flex items-center gap-1 px-2 z-20 whitespace-nowrap border-0 bg-transparent transition-transform duration-75 active:enabled:scale-[0.96]"
      :class="{ 'cursor-not-allowed': disabled || isReplyRestricted }"
      @click="$emit('selectMode', REPLY_EDITOR_MODES.REPLY)"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </button>
    <button
      ref="wootEditorPrivateMode"
      type="button"
      :disabled="disabled"
      class="flex items-center gap-1 px-2 z-20 whitespace-nowrap border-0 bg-transparent transition-transform duration-75 active:enabled:scale-[0.96]"
      :class="{ 'cursor-not-allowed': disabled }"
      @click="$emit('selectMode', REPLY_EDITOR_MODES.NOTE)"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </button>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :class="{
        'transition-all duration-300': !disabled && !isReplyRestricted,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </div>
</template>
